import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store } from './store';
import { fetchBDEs } from './store/bdeSlice';
import { fetchOrders } from './store/ordersSlice';
import { fetchShops } from './store/shopsSlice';
import Login from './pages/Login';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/Dashboard';
import BDEList from './pages/BDEList';
import BDEForm from './pages/BDEForm';
import Shops from './pages/Shops';
import ShopForm from './pages/ShopForm';
import ShopDetails from './pages/ShopDetails';
import AdminOrders from './pages/Orders';
import OrderEdit from './pages/OrderEdit';
import AdminOrderDetails from './pages/OrderDetails';
import PayoutHistory from './pages/admin/PayoutHistory';
import AdminProducts from './pages/admin/Products';
import QRManagement from './pages/admin/QRManagement';

// BDE Pages
import Register from './pages/bde/Register';
import OTP from './pages/bde/OTP';
import Pending from './pages/bde/Pending';
import BDEDashboard from './pages/bde/Dashboard';
import BDEOrderList from './pages/bde/OrderList';
import BDEOrderForm from './pages/bde/OrderForm';
import BDEOrderDetails from './pages/bde/OrderDetails';
import BDEShopList from './pages/bde/ShopList';
import BDEShopForm from './pages/bde/ShopForm';
import BDEProfile from './pages/bde/Profile';
import BDEPayoutHistory from './pages/bde/PayoutHistory';
import BDEHelp from './pages/bde/Help';
import NotFound from './pages/NotFound';

import MainLayout from './components/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';
import 'antd/dist/reset.css';

import { API_URL } from './utils/api';

const DataInitializer = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    // Initial fetch
    dispatch(fetchBDEs());
    dispatch(fetchOrders());
    dispatch(fetchShops());

    const es = new EventSource(`${API_URL}/events`);
    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.resource === 'users') dispatch(fetchBDEs());
        if (data.resource === 'orders') dispatch(fetchOrders());
        if (data.resource === 'shops') dispatch(fetchShops());
      } catch { }
    };
    return () => {
      es.close();
    };
  }, [dispatch]);
  return null;
};

function App() {
  return (
    <Provider store={store}>
      <DataInitializer />
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/register" element={<Register />} />
          <Route path="/otp" element={<OTP />} />
          <Route path="/pending" element={<Pending />} />

          <Route path="/" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
            <Route index element={<Navigate to="/login" replace />} />

            {/* Admin Routes */}
            <Route path="admin">
              <Route path="dashboard" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="bde-users" element={<ProtectedRoute allowedRoles={['super_admin']}><BDEList /></ProtectedRoute>} />
              <Route path="bde-users/:id/payouts" element={<ProtectedRoute allowedRoles={['super_admin']}><PayoutHistory /></ProtectedRoute>} />
              <Route path="payouts" element={<ProtectedRoute allowedRoles={['super_admin']}><PayoutHistory /></ProtectedRoute>} />
              <Route path="products" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminProducts /></ProtectedRoute>} />
              <Route path="qr-codes" element={<ProtectedRoute allowedRoles={['super_admin']}><QRManagement /></ProtectedRoute>} />
              <Route path="bde-users/create" element={<ProtectedRoute allowedRoles={['super_admin']}><BDEForm /></ProtectedRoute>} />
              <Route path="bde-users/edit/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><BDEForm /></ProtectedRoute>} />
              <Route path="shops" element={<ProtectedRoute allowedRoles={['super_admin']}><Shops /></ProtectedRoute>} />
              <Route path="shops/create" element={<ProtectedRoute allowedRoles={['super_admin']}><ShopForm /></ProtectedRoute>} />
              <Route path="shops/edit/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><ShopForm /></ProtectedRoute>} />
              <Route path="shops/view/:id" element={<ProtectedRoute allowedRoles={['super_admin']}><ShopDetails /></ProtectedRoute>} />
              <Route path="orders" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminOrders /></ProtectedRoute>} />
              <Route path="orders/edit/:orderId" element={<ProtectedRoute allowedRoles={['super_admin']}><OrderEdit /></ProtectedRoute>} />
              <Route path="orders/details/:orderId" element={<ProtectedRoute allowedRoles={['super_admin']}><AdminOrderDetails /></ProtectedRoute>} />
            </Route>

            {/* BDE Routes */}
            <Route path="bde">
              <Route path="dashboard" element={<ProtectedRoute allowedRoles={['bde']}><BDEDashboard /></ProtectedRoute>} />
              <Route path="shops" element={<ProtectedRoute allowedRoles={['bde']}><BDEShopList /></ProtectedRoute>} />
              <Route path="shops/create" element={<ProtectedRoute allowedRoles={['bde']}><BDEShopForm /></ProtectedRoute>} />
              <Route path="shops/edit/:id" element={<ProtectedRoute allowedRoles={['bde']}><BDEShopForm /></ProtectedRoute>} />
              <Route path="orders" element={<ProtectedRoute allowedRoles={['bde']}><BDEOrderList /></ProtectedRoute>} />
              <Route path="orders/create" element={<ProtectedRoute allowedRoles={['bde']}><BDEOrderForm /></ProtectedRoute>} />
              <Route path="orders/edit/:orderId" element={<ProtectedRoute allowedRoles={['bde']}><BDEOrderForm /></ProtectedRoute>} />
              <Route path="orders/details/:orderId" element={<ProtectedRoute allowedRoles={['bde']}><BDEOrderDetails /></ProtectedRoute>} />
              <Route path="payouts" element={<ProtectedRoute allowedRoles={['bde']}><BDEPayoutHistory /></ProtectedRoute>} />
              <Route path="profile" element={<ProtectedRoute allowedRoles={['bde']}><BDEProfile /></ProtectedRoute>} />
              <Route path="help" element={<ProtectedRoute allowedRoles={['bde']}><BDEHelp /></ProtectedRoute>} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </Router>
    </Provider>
  );
}

export default App;
